import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAssignees } from "@/api/hooks";
import { useCreateTag, useStatuses, useTags } from "@/api/work";
import type { StatusCategory } from "@/api/types/StatusCategory";
import type { Tag } from "@/api/types/Tag";
import type { TaskPriority } from "@/api/types/TaskPriority";
import type { TaskStatusRef } from "@/api/types/TaskStatusRef";
import type { TaskSummary } from "@/api/types/TaskSummary";
import type { UserRef } from "@/api/types/UserRef";
import { Icon, type IconName } from "@/components/Icon";
import { CategoryDot, StatusPill } from "@/components/Status";
import { useToast } from "@/components/Toast";
import { Avatar, BotTag, cx } from "@/components/ui";
import { fullDate } from "@/lib/format";
import { dayOffset, endOfDay, fromLocalInput, PALETTE, PRIORITIES, toLocalInput } from "@/lib/work";
import type { GuardTask } from "@/lib/testing";
import { useDoneGuard } from "./DoneGuard";
import { PickRow, PickSearch, Popover } from "./Popover";

const CATEGORIES: StatusCategory[] = ["not_started", "active", "done", "closed"];

/** The status, as a button that opens the project's statuses grouped by category. */
export function StatusPicker({
  projectKey,
  value,
  onChange,
  disabled,
  compact,
  pillClassName,
  shortcut,
  task,
}: {
  projectKey: string;
  value: TaskStatusRef;
  onChange: (id: string) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Sizing for the pill, e.g. to match a row of property chips. */
  pillClassName?: string;
  /** The task shortcut key that opens it (`data-shortcut`). */
  shortcut?: string;
  /** The task being changed: a move to Done with unpassed testing asks first. */
  task?: GuardTask;
}) {
  const { t } = useTranslation();
  const statuses = useStatuses(projectKey);
  const guard = useDoneGuard(projectKey);
  if (disabled) return <StatusPill status={value} className={pillClassName} />;
  return (
    <Popover
      width={230}
      trigger={({ toggle, props }) => (
        <button
          type="button"
          {...props}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}
          aria-label={t("work.changeStatus", { status: value.name })}
          data-shortcut={shortcut}
          className={cx("inline-flex max-w-full rounded-full", compact ? "" : "hover:opacity-85")}
        >
          <StatusPill status={value} className={pillClassName} />
        </button>
      )}
    >
      {(close) => (
        <div role="menu" className="flex flex-col overflow-y-auto p-1" onClick={(e) => e.stopPropagation()}>
          {CATEGORIES.map((c) => {
            const list = (statuses.data ?? []).filter((s) => s.category === c);
            if (!list.length) return null;
            return (
              <div key={c} className="flex flex-col">
                <span className="px-2 pt-1.5 pb-0.5 label-caps text-muted">{t(`work.category.${c}`)}</span>
                {list.map((s) => (
                  <PickRow
                    key={s.id}
                    selected={s.id === value.id}
                    onClick={() => {
                      close();
                      if (s.id !== value.id) (task ? guard([task], s.id, () => onChange(s.id)) : onChange(s.id));
                    }}
                  >
                    <CategoryDot category={s.category} />
                    <span className="truncate">{s.name}</span>
                  </PickRow>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </Popover>
  );
}

const PRIORITY_ICONS: Record<TaskPriority | "none", IconName> = {
  urgent: "priorityUrgent",
  high: "priorityHigh",
  medium: "priorityMedium",
  low: "priorityLow",
  none: "priorityNone",
};

/** A priority's icon: a red alert square for urgent, signal bars for high / medium / low, faint dashes for none. */
export function PriorityIcon({
  priority,
  size = 14,
  className,
  labelled,
}: {
  priority: TaskPriority | null;
  size?: number;
  className?: string;
  /** Give it a tooltip and an accessible name (where no text label sits next to it). */
  labelled?: boolean;
}) {
  const { t } = useTranslation();
  const p = priority ?? "none";
  const tone = p === "urgent" ? "text-fail-fg" : p === "none" ? "text-muted" : "text-text-2";
  const icon = <Icon name={PRIORITY_ICONS[p]} size={size} className={cx(tone, className)} />;
  if (!labelled) return icon;
  const label = t("work.priorityIs", { priority: t(`work.priorities.${p}`) });
  return (
    <span title={label} className="inline-flex shrink-0">
      {icon}
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Pick a priority, or none. */
export function PriorityPicker({
  value,
  onChange,
  trigger,
}: {
  value: TaskPriority | null;
  onChange: (p: TaskPriority | null) => void;
  trigger: (p: { toggle: () => void; props: Record<string, unknown> }) => ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Popover width={200} trigger={trigger}>
      {(close) => (
        <div role="menu" className="flex flex-col overflow-y-auto p-1" onClick={(e) => e.stopPropagation()}>
          {[...PRIORITIES, null].map((p) => (
            <PickRow
              key={p ?? "none"}
              selected={p === value}
              onClick={() => {
                close();
                if (p !== value) onChange(p);
              }}
            >
              <PriorityIcon priority={p} />
              {t(`work.priorities.${p ?? "none"}`)}
            </PickRow>
          ))}
        </div>
      )}
    </Popover>
  );
}

/** The priority filter chip: any of the levels, or "No priority". Values are the API's (`urgent` … `none`). */
export function PriorityFilter({
  value,
  onToggle,
  className,
  on,
  off,
}: {
  value: string[];
  onToggle: (p: string) => void;
  className: string;
  on: string;
  off: string;
}) {
  const { t } = useTranslation();
  const only = value.length === 1 ? value[0]! : null;
  return (
    <Popover
      width={210}
      trigger={({ toggle: open, props }) => (
        <button type="button" {...props} onClick={open} className={cx(className, value.length ? on : off)}>
          <PriorityIcon priority={only === "none" ? null : only ? (only as TaskPriority) : "high"} size={13} />
          {only ? t(`work.priorities.${only}`) : value.length ? t("work.nPriorities", { count: value.length }) : t("work.priority")}
        </button>
      )}
    >
      {() => (
        <div className="flex flex-col overflow-y-auto p-1">
          {[...PRIORITIES, "none" as const].map((p) => (
            <PickRow key={p} multi selected={value.includes(p)} onClick={() => onToggle(p)}>
              <PriorityIcon priority={p === "none" ? null : p} />
              {t(`work.priorities.${p}`)}
            </PickRow>
          ))}
        </div>
      )}
    </Popover>
  );
}

export function AvatarStack({ users, max = 3, size = 22 }: { users: UserRef[]; max?: number; size?: number }) {
  if (!users.length) return null;
  const shown = users.slice(0, max);
  return (
    <span className="inline-flex items-center" title={users.map((u) => u.display_name).join(", ")}>
      {shown.map((u, i) => (
        <span key={u.id} className={cx("rounded-full ring-2 ring-surface", i > 0 && "-ml-1.5")}>
          <Avatar name={u.display_name} bot={u.is_bot} size={size} />
        </span>
      ))}
      {users.length > max && <span className="ml-1 text-xs text-muted">+{users.length - max}</span>}
    </span>
  );
}

/** Pick people of the project. Multi-select toggles; single-select (`multi=false`) picks one. */
export function PeoplePicker({
  projectKey,
  selected,
  onToggle,
  multi = true,
  trigger,
  allowNone,
}: {
  projectKey: string;
  selected: string[];
  onToggle: (userId: string | null) => void;
  multi?: boolean;
  trigger: (p: { toggle: () => void; props: Record<string, unknown> }) => ReactNode;
  allowNone?: boolean;
}) {
  const { t } = useTranslation();
  const members = useAssignees(projectKey);
  const [q, setQ] = useState("");
  const list = (members.data ?? [])
    .filter((m) => !m.disabled)
    .filter((m) => `${m.display_name} ${m.username}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Popover width={250} trigger={trigger}>
      {(close) => (
        <>
          <PickSearch value={q} onChange={setQ} placeholder={t("work.searchPeople")} />
          <div role="menu" className="flex flex-col overflow-y-auto p-1">
            {allowNone && (
              <PickRow
                selected={selected.length === 0}
                onClick={() => {
                  onToggle(null);
                  close();
                }}
              >
                <span className="text-muted">{t("work.nobody")}</span>
              </PickRow>
            )}
            {list.map((m) => (
              <PickRow
                key={m.user_id}
                multi={multi}
                selected={selected.includes(m.user_id)}
                onClick={() => {
                  onToggle(m.user_id);
                  if (!multi) close();
                }}
              >
                <Avatar name={m.display_name} bot={m.is_bot} size={20} />
                <span className="min-w-0 flex-1 truncate">{m.display_name}</span>
                {m.is_bot && <BotTag />}
                <span className="text-xs text-muted">@{m.username}</span>
              </PickRow>
            ))}
            {members.isPending && <span className="px-2 py-1.5 text-sm text-muted">{t("common.loading")}</span>}
          </div>
        </>
      )}
    </Popover>
  );
}

export function TagChip({ tag, onRemove, className }: { tag: Tag; onRemove?: () => void; className?: string }) {
  const { t } = useTranslation();
  const color = tag.color ?? "#5c616c";
  return (
    <span
      className={cx("inline-flex items-center gap-1 rounded-[5px] px-1.5 text-xs leading-5 font-medium", className)}
      style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}
    >
      {tag.name}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={t("work.removeTag", { name: tag.name })} className="opacity-70 hover:opacity-100">
          <Icon name="x" size={11} />
        </button>
      )}
    </span>
  );
}

/** Toggle platform-wide tags; type a new name to create one. */
export function TagPicker({
  selected,
  onToggle,
  trigger,
}: {
  selected: string[];
  onToggle: (tag: Tag) => void;
  trigger: (p: { toggle: () => void; props: Record<string, unknown> }) => ReactNode;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const tags = useTags();
  const create = useCreateTag();
  const [q, setQ] = useState("");
  const query = q.trim();
  const list = (tags.data ?? []).filter((x) => x.name.toLowerCase().includes(query.toLowerCase()));
  const exact = (tags.data ?? []).some((x) => x.name.toLowerCase() === query.toLowerCase());
  const make = () => {
    if (!query || exact) return;
    const color = PALETTE[(tags.data?.length ?? 0) % PALETTE.length]!;
    create.mutate(
      { name: query, color },
      {
        onSuccess: (tag) => {
          setQ("");
          onToggle(tag);
        },
        onError: (e) => toast.error(e),
      },
    );
  };
  return (
    <Popover width={240} trigger={trigger}>
      {() => (
        <>
          <PickSearch value={q} onChange={setQ} placeholder={t("work.searchTags")} onEnter={make} />
          <div role="menu" className="flex flex-col overflow-y-auto p-1">
            {list.map((tag) => (
              <PickRow key={tag.id} multi selected={selected.includes(tag.id)} onClick={() => onToggle(tag)}>
                <TagChip tag={tag} />
              </PickRow>
            ))}
            {query && !exact && (
              <PickRow onClick={make}>
                <Icon name="plus" size={14} />
                {t("work.createTag", { name: query })}
              </PickRow>
            )}
            {!list.length && !query && <span className="px-2 py-1.5 text-sm text-muted">{t("work.noTags")}</span>}
          </div>
        </>
      )}
    </Popover>
  );
}

/** "Today", "Tomorrow", "Mon 3 Nov" or "3 Nov 2027"; red when overdue. */
export function DueLabel({ task, empty }: { task: Pick<TaskSummary, "due_at" | "is_overdue">; empty?: ReactNode }) {
  const { t, i18n } = useTranslation();
  if (!task.due_at) return <>{empty ?? null}</>;
  const d = dayOffset(task.due_at);
  const date = new Date(task.due_at);
  const text =
    d === 0
      ? t("work.today")
      : d === 1
        ? t("work.tomorrow")
        : d === -1
          ? t("work.yesterday")
          : date.toLocaleDateString(i18n.language, {
              day: "numeric",
              month: "short",
              ...(date.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : { weekday: "short" }),
            });
  return (
    <span
      title={fullDate(task.due_at)}
      className={cx(
        "inline-flex items-center gap-1 text-xs whitespace-nowrap tabular-nums",
        task.is_overdue ? "font-semibold text-fail-fg" : d <= 1 ? "font-semibold text-blocked-fg" : "text-muted",
      )}
    >
      <Icon name="calendar" size={12} />
      {text}
    </span>
  );
}

/** Due date: quick picks plus an exact date and time. */
export function DuePicker({
  value,
  onChange,
  trigger,
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
  trigger: (p: { toggle: () => void; props: Record<string, unknown> }) => ReactNode;
}) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(toLocalInput(value));
  const quick: [string, string][] = [
    ["work.today", endOfDay(0)],
    ["work.tomorrow", endOfDay(1)],
    ["work.inAWeek", endOfDay(7)],
  ];
  return (
    <Popover width={250} trigger={(p) => trigger(p)}>
      {(close) => (
        <div className="flex flex-col gap-2 p-2">
          <div className="flex flex-col">
            {quick.map(([label, iso]) => (
              <PickRow
                key={label}
                onClick={() => {
                  onChange(iso);
                  close();
                }}
              >
                <Icon name="calendar" size={14} className="text-muted" />
                {t(label)}
              </PickRow>
            ))}
          </div>
          <input
            type="datetime-local"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            aria-label={t("work.dueAt")}
            className="h-9 rounded-md border border-border-strong bg-surface px-2 text-sm text-text"
          />
          <div className="flex justify-between gap-2">
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sm text-muted hover:bg-surface-2 hover:text-text"
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              {t("work.clearDue")}
            </button>
            <button
              type="button"
              className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-50"
              disabled={!local}
              onClick={() => {
                onChange(fromLocalInput(local));
                close();
              }}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

/** The small counters a row or card shows: subtasks, checklist, comments, blockers. */
export function TaskCounters({ task }: { task: TaskSummary }) {
  const { t } = useTranslation();
  const items: { icon: Parameters<typeof Icon>[0]["name"]; text: string; label: string; tone?: string }[] = [];
  if (task.subtask_progress.total > 0)
    items.push({
      icon: "subtask",
      text: `${task.subtask_progress.done}/${task.subtask_progress.total}`,
      label: t("work.subtasksDone", { done: task.subtask_progress.done, total: task.subtask_progress.total }),
    });
  if (task.checklist_progress.total > 0)
    items.push({
      icon: "checklist",
      text: `${task.checklist_progress.done}/${task.checklist_progress.total}`,
      label: t("work.checklistDone", { done: task.checklist_progress.done, total: task.checklist_progress.total }),
    });
  if (task.comment_count > 0)
    items.push({
      icon: "message",
      text: String(task.comment_count),
      label: t("work.comments", { count: task.comment_count }),
      tone: task.unread_comment_count > 0 ? "text-accent font-semibold" : undefined,
    });
  if (task.open_blocker_count > 0)
    items.push({
      icon: "blocked",
      text: String(task.open_blocker_count),
      label: t("work.blockedBy", { count: task.open_blocker_count }),
      tone: "text-blocked-fg font-semibold",
    });
  if (!items.length) return null;
  return (
    <span className="inline-flex items-center gap-2.5 text-xs text-muted">
      {items.map((i) => (
        <span key={i.icon} title={i.label} className={cx("inline-flex items-center gap-1 tabular-nums", i.tone)}>
          <Icon name={i.icon} size={12} />
          {i.text}
          <span className="sr-only">{i.label}</span>
        </span>
      ))}
    </span>
  );
}
